import React from 'react';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import { PerfectScrollbar } from 'components/PerfectScrollbar';
import { style } from 'typestyle';
import { ThemeStyle, ADALONG_COLORS } from '@src/styles';
import { ThemeProvider, createTheme, CircularProgress } from '@mui/material';

const materialTheme = createTheme({
  components: {
    MuiTable: {
      styleOverrides: {
        root: {
          width: '95%',
          margin: 'auto',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px 5px',
          top: 0,
          color: 'white !important',
          fontSize: `${ThemeStyle.textSizes.big}px !important`,
          fontWeight: '300 !important',
          fontFamily: ThemeStyle.fonts.primary,
          borderBottom: 'none',
        },
        head: {
          position: 'sticky',
          borderBottom: '1px solid #444',
          background: ADALONG_COLORS.SMOOTH_BLACK,
          textTransform: 'uppercase',
          fontWeight: '700 !important',
          fontSize: `${ThemeStyle.textSizes.small}px !important`,
          paddingTop: 16,
        },
        body: {
          borderBottom: '1px solid rgba(255,255,255,0.04)',
        },
      },
    },
  },
} as any);

export interface ListColumn<T> {
  label: React.ReactNode
  displayName: (elem: T) => string | JSX.Element
  onClick?: (elem: T) => any
  minWidth?: string | number
}

interface Props<T> {
  list: T[]
  columns: ListColumn<T>[]
  emptyMessage: string
  loading?: boolean
}

export function TableList<T>({ list, columns, emptyMessage, loading }: Props<T>) {
  return (
      <div className={contentClass}>
        <ThemeProvider theme={materialTheme}>
          {list.length ? (
              <PerfectScrollbar>
                <Table>

                  <TableHead>
                    <TableRow>
                      {columns.map((element, idx) => (
                          <TableCell
                              key={idx}
                              align="left"
                              style={{ minWidth: element.minWidth || 10 }}
                          >
                            {element.label}
                          </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {list.map((elem, idx) => (
                        <TableRow
                            className={listClass}
                            hover role="checkbox"
                            key={idx}
                        >
                          {columns.map((column, col_idx) => {
                            const child = column.displayName(elem);
                            const { onClick } = columns[col_idx];
                            return (
                                <TableCell onClick={() => onClick && onClick(elem)} key={`${idx}-${col_idx}`} align="left">
                                  {child}
                                </TableCell>
                            );
                          })}
                        </TableRow>
                    ))}

                  </TableBody>

                </Table>
              </PerfectScrollbar>
          ) : (
            !loading ? (<div className={nothingClass}>{emptyMessage}</div>) : null
          )}
          {loading ? (
              <div style={{
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                paddingTop: 80,
              }}
              >
                <CircularProgress/>
              </div>
          ) : null}
        </ThemeProvider>
      </div>
  );
}

const contentClass = style({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: 400,
});

const listClass = style({
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  $nest: {
    '&:hover': {
      backgroundColor: '#313131 !important',
      color: 'gray !important',
    },
  },
});

const nothingClass = style({
  textAlign: 'center',
  fontSize: 20,
  color: 'gray',
  marginTop: 12,
});
